import { supabase } from "../lib/supabase";
import { extractFunctionErrorMessage } from "./edgeFunctionErrors";

async function errorBody(error) {
  try {
    return error?.context?.json ? await error.context.clone().json() : null;
  } catch {
    return null;
  }
}

// True when the creative has a variation, i.e. create_asset claimed it. A failed lookup
// counts as none.
async function hasVariation(creativeId) {
  const { data, error } = await supabase
    .from("creative_variations")
    .select("id")
    .eq("creative_id", creativeId)
    .limit(1);
  return !error && Array.isArray(data) && data.length > 0;
}

// Turns a staged upload into a Portal asset through the portal-api Edge Function's
// creatives/create_asset action. On success the variation is "processing", not playable yet.
// Returns { error, handedOff }. Every answer the function gives after it claimed the creative
// carries variation_id, so such an answer is handed off, and so is a 409 (already claimed).
// A 4xx without variation_id is a refusal before the claim. Anything else is ambiguous: no
// answer at all (network error, function killed) or a 5xx without variation_id, which the
// platform also sends as JSON (546 WORKER_RESOURCE_LIMIT, gateway errors) when the function
// may have been stopped after claiming. It is retried once, which the claim makes safe (a 409
// means the first call got there). If the retry is ambiguous too, the creative counts as
// handed off only when it has a variation: without one nothing will ever pick it up, and
// keeping it would leave a creative that never shows as processing or failed. A handed-off
// creative is kept, never deleted.
export async function startCreativeProcessing({ advertiserId, creativeId, storagePath }) {
  if (!supabase) return { error: new Error("Supabase not configured"), handedOff: false };
  let error;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    ({ error } = await supabase.functions.invoke("portal-api", {
      body: {
        resource: "creatives",
        action: "create_asset",
        advertiser_id: advertiserId,
        creative_id: creativeId,
        storage_path: storagePath,
      },
    }));
    if (!error) return { error: null, handedOff: true };
    const body = await errorBody(error);
    const status = error?.context?.status;
    if (body?.variation_id != null || status === 409) {
      return { error: new Error(await extractFunctionErrorMessage(error)), handedOff: true };
    }
    if (body != null && status < 500) {
      return { error: new Error(await extractFunctionErrorMessage(error)), handedOff: false };
    }
  }
  return { error: new Error(await extractFunctionErrorMessage(error)), handedOff: await hasVariation(creativeId) };
}

// Edits a creative's name, inventory types and dates through the portal-api Edge Function,
// which updates its Portal assets first and then the creative. Returns { error }.
export async function updateCreativeDetails({ advertiserId, creativeId, name, startDate, endDate, audioTypes }) {
  if (!supabase) return { error: new Error("Supabase not configured") };
  const { error } = await supabase.functions.invoke("portal-api", {
    body: {
      resource: "creatives",
      action: "update_details",
      advertiser_id: advertiserId,
      creative_id: creativeId,
      name,
      start_date: startDate,
      end_date: endDate,
      audio_types: audioTypes,
    },
  });
  return { error: error ? new Error(await extractFunctionErrorMessage(error)) : null };
}
